import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import client from "@/lib/api";
import { logger } from "@/lib/logger";

const formatKes = (v) => `KES ${Number(v).toLocaleString("en-KE")}`;

/**
 * Hosted-redirect checkout for Paystack (Kenya).
 * Collects name / email / (optional) phone → POST /payments/paystack/init →
 * redirect to Paystack's authorization_url. Buyer picks M-PESA or Card on
 * Paystack's page and lands back on /payment/success?reference=...
 */
export default function PaystackCheckoutModal({ open, onClose, product, prefEmail }) {
  const [email, setEmail] = useState(prefEmail || "");
  const [phone, setPhone] = useState("");
  const [fullname, setFullname] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { setEmail(prefEmail || ""); }, [prefEmail]);

  useEffect(() => {
    if (!open) {
      setLoading(false);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!product) return;
    setLoading(true);
    try {
      const { data } = await client.post("/payments/paystack/init", {
        product_id: product.id,
        email,
        fullname,
        phone_number: phone || null,
      });
      // Full page redirect to Paystack's hosted checkout (or the mock success URL in sandbox).
      window.location.href = data.authorization_url;
    } catch (err) {
      logger.error("Paystack init failed:", err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start Paystack checkout");
      setLoading(false);
    }
  };

  if (!open || !product) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="paystack-modal"
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-mizizi-charcoal/70 backdrop-blur-sm"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-md bg-mizizi-cream text-mizizi-charcoal shadow-2xl border border-mizizi-gold/40">
        <button
          type="button"
          onClick={onClose}
          data-testid="paystack-close"
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full hover:bg-mizizi-charcoal/10 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-8 md:p-10">
          <div className="overline text-mizizi-terracotta">Pay with M-PESA or Card</div>
          <h3 className="font-serif text-3xl mt-3 leading-tight">{product.name}</h3>
          <div className="font-serif text-2xl text-mizizi-gold mt-2">{formatKes(product.price_kes)}</div>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="overline text-mizizi-charcoal/70 block mb-1" htmlFor="paystack-name">Full name</label>
              <input
                id="paystack-name"
                data-testid="paystack-name"
                required minLength={2}
                value={fullname}
                onChange={(e) => setFullname(e.target.value)}
                className="w-full px-4 py-3 bg-transparent border border-mizizi-gold/50 focus:outline-none focus:border-mizizi-terracotta text-base min-h-[48px]"
              />
            </div>

            <div>
              <label className="overline text-mizizi-charcoal/70 block mb-1" htmlFor="paystack-email">Email</label>
              <input
                id="paystack-email"
                type="email"
                required
                data-testid="paystack-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-transparent border border-mizizi-gold/50 focus:outline-none focus:border-mizizi-terracotta text-base min-h-[48px]"
              />
            </div>

            <div>
              <label className="overline text-mizizi-charcoal/70 block mb-1" htmlFor="paystack-phone">
                M-PESA phone <span className="text-mizizi-charcoal/40 normal-case">(optional)</span>
              </label>
              <input
                id="paystack-phone"
                data-testid="paystack-phone"
                placeholder="0712 345 678"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-4 py-3 bg-transparent border border-mizizi-gold/50 focus:outline-none focus:border-mizizi-terracotta text-base min-h-[48px]"
              />
              <p className="text-xs text-mizizi-charcoal/60 mt-1">
                Only needed if you'll pay by M-PESA. You'll pick the method on the Paystack page.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="paystack-submit"
              className="w-full pill pill-primary disabled:opacity-60"
            >
              {loading ? "Redirecting…" : `Continue · ${formatKes(product.price_kes)}`}
            </button>

            <p className="text-[11px] text-mizizi-charcoal/50 text-center pt-2">
              Secure checkout by Paystack · M-PESA and Cards accepted
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
